import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Plane, Trash2 } from "lucide-react";
import { TripForm, daysUntil, DEFAULT_TRIP_IMG } from "@/components/TravelWidget";

const STATUS_STYLE = {
  dreaming: "text-zinc-400 border-white/15 bg-white/5",
  planned: "text-[#00E5FF] border-[#00E5FF]/40 bg-[#00E5FF]/10",
  booked: "text-emerald-400 border-emerald-500/40 bg-emerald-500/10",
};

const fmtDate = (d, withYear = false) => {
  if (!d) return "";
  return new Date(d + "T00:00:00").toLocaleDateString("en-AU", { day: "numeric", month: "short", ...(withYear ? { year: "numeric" } : {}) });
};

const countdown = (t) => {
  const start = daysUntil(t.start_date);
  const end = daysUntil(t.end_date);
  if (start !== null && start > 0) return { label: `${start} days away`, cls: "text-black bg-[#D4AF37]" };
  if (start !== null && start <= 0 && (end === null || end >= 0)) return { label: "Underway", cls: "text-black bg-emerald-400" };
  if (start !== null) return { label: "Completed", cls: "text-zinc-300 bg-white/10" };
  return null;
};

export default function Travel() {
  const [trips, setTrips] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/trips");
    setTrips(data.trips);
  }, []);
  useEffect(() => { load().catch((err) => { console.error("Trips load failed:", err); toast.error("Failed to load trips"); }); }, [load]);

  const remove = async (id) => {
    try {
      await api.delete(`/trips/${id}`);
      toast.success("Trip removed");
      load();
    } catch { toast.error("Couldn't remove trip"); }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-zinc-100" data-testid="travel-page">
      <section className="relative overflow-hidden border-b border-white/10">
        <img src={DEFAULT_TRIP_IMG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-black/70" />
        <div className="relative max-w-7xl mx-auto px-6 pt-8 pb-14">
          <Link to="/" className="flex w-fit items-center gap-2 text-xs uppercase tracking-widest text-zinc-400 hover:text-[#D4AF37] transition-colors duration-200 mb-10" data-testid="back-to-dashboard">
            <ArrowLeft className="w-4 h-4" /> Dashboard
          </Link>
          <p className="text-[10px] uppercase tracking-[0.4em] text-[#00E5FF] inline-flex items-center gap-2 mb-3">
            <Plane className="w-3.5 h-3.5" /> Always something to look forward to
          </p>
          <h1 className="font-display text-4xl sm:text-5xl text-white" data-testid="travel-heading">Travel & Experiences</h1>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-10">
        <div className="bg-[#0F0F11] border border-white/10 rounded-md p-6 mb-10" data-testid="travel-add-section">
          <h2 className="font-display text-xl text-white mb-4">Plan the Next One</h2>
          <TripForm onSaved={load} />
        </div>

        {!trips ? (
          <div className="flex justify-center py-16" data-testid="travel-loading">
            <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : trips.length === 0 ? (
          <p className="text-sm text-zinc-500" data-testid="travel-empty">No trips yet. The first entry above is the hardest.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="trips-grid">
            {trips.map((t, i) => {
              const cd = countdown(t);
              return (
                <motion.div
                  key={t.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.06 * i }}
                  className="group relative h-72 rounded-md overflow-hidden border border-white/10 hover:border-[#D4AF37]/50 transition-colors duration-300"
                  data-testid={`trip-card-${t.id}`}
                >
                  <img src={t.image_url || DEFAULT_TRIP_IMG} alt={t.destination} className="absolute inset-0 w-full h-full object-cover opacity-45" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
                  <button
                    onClick={() => remove(t.id)}
                    className="absolute top-3 right-3 z-10 p-1.5 bg-black/60 backdrop-blur-md border border-white/10 rounded-sm text-zinc-400 hover:text-red-400 transition-colors duration-200 sm:opacity-0 sm:group-hover:opacity-100"
                    data-testid={`trip-delete-${t.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                  <div className="relative h-full flex flex-col justify-end p-6">
                    <h3 className="font-display text-2xl text-white">{t.destination}</h3>
                    <p className="text-sm text-zinc-400 mt-0.5">{t.title}</p>
                    {t.notes && <p className="text-xs text-zinc-500 mt-2 line-clamp-2">{t.notes}</p>}
                    <div className="flex flex-wrap items-center gap-2 mt-4">
                      {t.start_date && (
                        <span className="text-[10px] text-zinc-300 border border-white/10 bg-black/40 px-2.5 py-1 rounded-sm" data-testid={`trip-dates-${t.id}`}>
                          {fmtDate(t.start_date)}{t.end_date ? ` → ${fmtDate(t.end_date, true)}` : ""}
                        </span>
                      )}
                      {cd && <span className={`text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-sm ${cd.cls}`} data-testid={`trip-countdown-${t.id}`}>{cd.label}</span>}
                      <span className={`text-[9px] uppercase tracking-[0.2em] px-2.5 py-1 rounded-sm border ${STATUS_STYLE[t.status]}`} data-testid={`trip-status-${t.id}`}>{t.status}</span>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
